import { ICompileService } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import {
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsServiceName as FormViewServiceName,
  vsProviderName as FormViewServiceProviderName,
  FormSubmitArgs,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import { vsLookupManagementServiceProviderName as LookupManagementServiceProviderName } from "../../shared/lookups";
import {
  LookupManagementConfig,
  LookupManagementServiceProvider,
} from "../../../records/lookup/lookupManagement";
import { MachineModel, MachineModelView } from "../models/machine";

export const vsObjectName = "machineModel";

const url = "/api/machines/MachineModel";

MachineModelConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  LookupManagementServiceProviderName,
];
export function MachineModelConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  lookupManagementServiceProvider: LookupManagementServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    vsObjectName,
    getGridConfig
  );
  formViewServiceProvider.addFormViewConfig(vsObjectName, getFormConfig);

  lookupManagementServiceProvider.addLookup(vsObjectName, {
    displayName: "Model",
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility) => {
        const config: LookupManagementConfig = {
          fetchFormModel(id) {
            return serverUtility.querySingle({
              url,
              query: { Id: id },
            });
          },
          baseUrl: url,
          getLongItemDisplay: (model: MachineModelView) =>
            `${model.MachineManufacturerName}-${model.MachineModelTypeName}-${model.Name}`,
        };
        return config;
      },
    ],
  });
}

getGridConfig.$inject = [
  ColumnDefinitionServiceName,
  "$compile",
  ServerUtilityName,
];
function getGridConfig(
  columnDefinitionService: ColumnDefinitionService,
  $compile: ICompileService,
  serverUtility: ServerUtility
): GridConfiguration {
  const config: GridConfiguration = {
    columns: [
      columnDefinitionService.getNumberColDef({ field: "Id", hide: true }),
      columnDefinitionService.getStringColDef({
        field: "MachineManufacturerName",
        header: "Manufacturer",
        filter: new FilterProvider("MachineManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineManufacturerId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineManufacturer",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineModelTypeName",
        header: "Model Type",
        filter: new FilterProvider("MachineModelTypeName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineModelTypeId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineModelType",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Name",
        header: "Model",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "Disabled",
      }),
    ],
    gridDataSource: new RecordDataSource(serverUtility, url),
    defaultSort: "MachineManufacturerName,MachineModelTypeName,Name",
    namePlural: "Models",
    nameSingular: "Model",
    showAddButton: true,
    showAddButtonPermission: "machineLookupModify",
    showSelectButtons: false,
  };
  return config;
}

getFormConfig.$inject = [
  FormViewServiceName,
  FieldConfigurationHelperServiceName,
];
function getFormConfig(
  formViewService: FormViewService,
  fieldConfigurationHelperService: FieldConfigurationHelperService
): FormViewConfig {
  return {
    fields: [
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineManufacturerId",
        name: "MachineManufacturerId",
        label: "Manufacturer",
        required: true,
        getDefaultItem: (model) => {
          return {
            Id: model.MachineManufacturerId,
            Name: model.MachineManufacturerName,
          };
        },
        queryUrl: "/api/machines/MachineManufacturer",
      }),
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineModelTypeId",
        name: "MachineModelTypeId",
        label: "Model Type",
        required: true,
        disableDefaultQueryCaching: true,
        getDefaultItem: (model) => {
          return {
            Id: model.MachineModelTypeId,
            Name: model.MachineModelTypeName,
          };
        },
        expressionProperties: {
          "templateOptions.readonly": (_, __, scope) =>
            scope.model?.MachineManufacturerId === undefined,
        },
        watcher: [
          {
            expression: "model.MachineManufacturerId",
            listener: (_, newValue, oldValue, scope) => {
              if (scope.model && newValue !== oldValue) {
                scope.model.MachineModelTypeId = undefined;
              }
            },
          },
        ],
        queryBuilder: (search, model) => {
          return {
            MachineManufacturerId: model.MachineManufacturerId,
            NameContains: search,
          };
        },
        queryUrl: "/api/machines/MachineModelType",
      }),
      fieldHelpers.simpleStringField({
        key: "Name",
        label: "Model",
        required: true,
      }),
      fieldHelpers.simpleBoolField({
        key: "Disabled",
        defaultValue: false,
      }),
    ],
    title: (model) => (model.Id ? "Edit Model" : "Create Model"),
    name: vsObjectName,
    submitForm: (args: FormSubmitArgs) => {
      const model = <MachineModel>args.model;
      return formViewService.saveForm({
        formObject: model,
        formViewController: args.formViewController,
        url,
      });
    },
  };
}
